// src/processors/template/template-processor.ts - Main Template Processor
import {TemplateContentProcessor} from './template-content-processor';
import {TemplateSourceMapProcessor} from './template-sourcemap-processor';
import {TemplateFileHandler} from './template-file-handler';
import {Logger} from "../../utils/logger";
import {XConVitePluginOptions, ProcessResult} from "../../types";

/**
 * Main template processor that orchestrates all template processing operations
 */
export class TemplateProcessor {
    private contentProcessor: TemplateContentProcessor;
    private sourceMapProcessor: TemplateSourceMapProcessor;
    private fileHandler: TemplateFileHandler;

    constructor(
        private options: Required<XConVitePluginOptions>,
        private logger: Logger
    ) {
        this.contentProcessor = new TemplateContentProcessor(options, logger);
        this.sourceMapProcessor = new TemplateSourceMapProcessor(options, logger);
        this.fileHandler = new TemplateFileHandler(options, logger);
    }

    /**
     * Process templateUrl -> template conversion for HTML files with source maps
     */
    processTemplateUrl(
        code: string,
        id: string,
        addWatchFile: (file: string) => void
    ): ProcessResult & { map?: any } {
        // If inline template already exists, skip templateUrl processing
        if (this.hasInlineTemplate(code)) {
            this.logger.debug('Inline template found, skipping templateUrl processing');
            return { code, changed: false };
        }

        // Check if source maps are requested and MagicString is available
        if (this.options.sourceMap && this.sourceMapProcessor.isMagicStringAvailable()) {
            return this.processWithSourceMap(code, id, addWatchFile);
        } else {
            return this.processWithoutSourceMap(code, id, addWatchFile);
        }
    }

    /**
     * Check if inline template already exists in the code (not in comments)
     */
    private hasInlineTemplate(code: string): boolean {
        const templateRegex = /template:\s*[`'"]/g;
        let match;

        while ((match = templateRegex.exec(code)) !== null) {
            // If we find a template that's not in a comment, return true
            if (!this.isInComment(code, match.index)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if a position in code is within a comment
     */
    private isInComment(code: string, position: number): boolean {
        const beforeMatch = code.substring(0, position);
        const lines = beforeMatch.split('\n');
        const currentLine = lines[lines.length - 1];

        // Check for single-line comment
        const singleLineCommentIndex = currentLine.indexOf('//');
        if (singleLineCommentIndex !== -1 && singleLineCommentIndex < currentLine.length) {
            return true;
        }

        // Check for multi-line comment
        const lastBlockCommentStart = beforeMatch.lastIndexOf('/*');
        const lastBlockCommentEnd = beforeMatch.lastIndexOf('*/');

        if (lastBlockCommentStart !== -1 && lastBlockCommentStart > lastBlockCommentEnd) {
            return true;
        }

        return false;
    }

    /**
     * Process with source map support
     */
    private processWithSourceMap(
        code: string,
        id: string,
        addWatchFile: (file: string) => void
    ): ProcessResult & { map?: any } {
        const magicString = this.sourceMapProcessor.createMagicString(code);
        if (!magicString) {
            this.logger.warn('Failed to create MagicString, falling back to basic processing');
            return this.processWithoutSourceMap(code, id, addWatchFile);
        }

        let hasChanges = false;
        const matches = this.findTemplateUrlMatches(code);

        // Process matches in reverse order to maintain correct positions
        matches.reverse().forEach(({templatePath, start, end}) => {
            try {
                const replacement = this.processTemplateMatch(templatePath, id, addWatchFile);
                magicString.overwrite(start, end, replacement);
                hasChanges = true;
            } catch (error) {
                this.logger.error(`Template processing error for ${templatePath}:`, error);
                magicString.overwrite(start, end, `template: ''`);
                hasChanges = true;
            }
        });

        if (hasChanges) {
            return {
                code: magicString.toString(),
                changed: hasChanges,
                map: this.sourceMapProcessor.generateSourceMap(magicString, id)
            };
        }

        return {code, changed: false};
    }

    /**
     * Process without source map support
     */
    private processWithoutSourceMap(
        code: string,
        id: string,
        addWatchFile: (file: string) => void
    ): ProcessResult {
        let transformedCode = code;
        let hasChanges = false;

        transformedCode = transformedCode.replace(
            /templateUrl:\s*['"](.*?\.html)['"]/g,
            (match, templatePath, offset) => {
                // Skip if match is within a comment
                if (this.isInComment(code, offset)) {
                    this.logger.debug(`Skipping commented templateUrl: ${templatePath}`);
                    return match;
                }

                try {
                    hasChanges = true;
                    return this.processTemplateMatch(templatePath, id, addWatchFile);
                } catch (error) {
                    this.logger.error(`Template error:`, error);
                    hasChanges = true;
                    return `template: ''`;
                }
            }
        );

        return {code: transformedCode, changed: hasChanges};
    }

    /**
     * Find all templateUrl matches in code (HTML files only)
     */
    private findTemplateUrlMatches(code: string): Array<{
        match: string;
        templatePath: string;
        start: number;
        end: number;
    }> {
        const templateUrlRegex = /templateUrl:\s*['"](.*?\.html)['"]/g;
        const matches: Array<{
            match: string;
            templatePath: string;
            start: number;
            end: number;
        }> = [];

        let match;
        while ((match = templateUrlRegex.exec(code)) !== null) {
            // Skip if this match is within a comment
            if (this.isInComment(code, match.index)) {
                this.logger.debug(`Skipping commented templateUrl: ${match[1]}`);
                continue;
            }

            matches.push({
                match: match[0],
                templatePath: match[1],
                start: match.index,
                end: match.index + match[0].length
            });
        }

        return matches;
    }

    /**
     * Process individual template match for HTML files
     */
    private processTemplateMatch(
        templatePath: string,
        id: string,
        addWatchFile: (file: string) => void
    ): string {
        const templateContent = this.fileHandler.loadTemplateFile(templatePath, id, addWatchFile);

        if (templateContent === null) {
            this.logger.warn(`HTML template not found: ${templatePath}`);
            return `template: ''`;
        }

        const processedContent = this.contentProcessor.processTemplateContent(templateContent);
        this.logger.debug(`Loaded HTML template: ${templatePath} (${processedContent.length} chars)`);

        return `template: \`${processedContent}\``;
    }
}